import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Flag, Loader2, NotebookPen, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AGENT_TURN_DONE_EVENT } from '../lib/agents';
import type { Task, TaskStatus } from '../lib/types';
import { InlineError } from './InlineError';

export const TASK_STATUSES: { id: TaskStatus; label: string }[] = [
    { id: 'to_do', label: 'To do' },
    { id: 'in_progress', label: 'In progress' },
    { id: 'done', label: 'Done' },
];

export const isOpenTask = (t: Task) => t.status !== 'done';

export const formatDueDate = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// The workspace's tasks, all of them or one area's (with its sub-areas'). Reloaded
// after each agent turn, since agents are what add most of them.
export function useTasks(sectionId?: string) {
    const { authFetch } = useAuth();
    const [tasks, setTasks] = useState<Task[] | null>(null);

    const load = useCallback(() => {
        const query = sectionId ? `?section_id=${encodeURIComponent(sectionId)}` : '';
        authFetch(`/tasks${query}`)
            .then(r => r.ok ? r.json() : [])
            .then(setTasks)
            .catch(() => setTasks([]));
    }, [authFetch, sectionId]);

    useEffect(load, [load]);
    useEffect(() => {
        window.addEventListener(AGENT_TURN_DONE_EVENT, load);
        return () => window.removeEventListener(AGENT_TURN_DONE_EVENT, load);
    }, [load]);

    return { tasks, reload: load };
}

export function useTaskUpdate(onChanged: () => void) {
    const { authFetch } = useAuth();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = async (path: string, init: RequestInit) => {
        setBusy(true);
        setError(null);
        try {
            const res = await authFetch(path, init);
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || 'Could not save that change');
            }
            onChanged();
            return true;
        } catch (e: any) {
            setError(e.message);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const update = (task: Task, fields: Partial<Task>) => run(`/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
    });
    const remove = (task: Task) => run(`/tasks/${task.id}`, { method: 'DELETE' });

    return { update, remove, busy, error };
}

export default function TaskCard({ task, onChanged }: { task: Task; onChanged: () => void }) {
    const { update, remove, busy, error } = useTaskUpdate(onChanged);
    const [editingNotes, setEditingNotes] = useState(false);
    const [notes, setNotes] = useState(task.notes ?? '');
    const done = task.status === 'done';

    const saveNotes = async () => {
        if (await update(task, { notes: notes.trim() || null })) setEditingNotes(false);
    };

    return (
        <div className={`border rounded-xl p-4 ${done ? 'border-softgrey/70 bg-offwhite/50' : 'border-softgrey bg-white'}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${done ? 'text-coolslate line-through' : 'text-graphite'}`}>{task.title}</p>
                    {task.description && <p className="text-[13px] text-coolslate mt-0.5">{task.description}</p>}
                </div>
                <div role="group" aria-label="Status" className="inline-flex rounded-lg border border-softgrey overflow-hidden shrink-0">
                    {TASK_STATUSES.map(s => (
                        <button
                            key={s.id}
                            onClick={() => s.id !== task.status && update(task, { status: s.id })}
                            disabled={busy}
                            aria-pressed={s.id === task.status}
                            className={`px-2.5 py-1 text-[12px] font-medium transition-colors ${s.id === task.status
                                ? 'bg-brand text-white'
                                : 'bg-white text-coolslate hover:text-graphite'}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {(task.due_on || task.created_by || task.milestone_id) && (
                <p className="mt-2 text-[11px] text-coolslate flex flex-wrap items-center gap-x-3 gap-y-1">
                    {task.due_on && (
                        <span className="inline-flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" /> Due {formatDueDate(task.due_on)}
                        </span>
                    )}
                    {task.milestone_id && (
                        <Link to="/plans" className="inline-flex items-center gap-1 text-brand-dark hover:underline">
                            <Flag className="w-3 h-3" /> Towards a milestone
                        </Link>
                    )}
                    {task.created_by && <span>Added by {task.created_by}</span>}
                </p>
            )}

            {editingNotes ? (
                <div className="mt-3">
                    <textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        rows={3}
                        aria-label="Notes"
                        className="w-full text-sm px-3 py-2 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand"
                    />
                    <div className="mt-2 flex items-center gap-3">
                        <button
                            onClick={saveNotes}
                            disabled={busy}
                            className="navaris-btn-compact"
                        >
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Save
                        </button>
                        <button
                            onClick={() => { setEditingNotes(false); setNotes(task.notes ?? ''); }}
                            className="text-[13px] font-medium text-coolslate hover:text-graphite"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : task.notes && (
                <p className="mt-3 text-[13px] text-graphite whitespace-pre-wrap break-words">{task.notes}</p>
            )}

            {error && <div className="mt-3"><InlineError message={error} /></div>}

            {!editingNotes && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => setEditingNotes(true)}
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand hover:text-brand-dark transition-colors"
                    >
                        <NotebookPen className="w-3.5 h-3.5" />
                        {task.notes ? 'Edit notes' : 'Add notes'}
                    </button>
                    <button
                        onClick={() => window.confirm(`Remove "${task.title}"?`) && remove(task)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-coolslate hover:text-graphite transition-colors"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                    </button>
                </div>
            )}
        </div>
    );
}
